import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Phone, Mail, PawPrint, Save, CheckCircle2, Shield, Calendar, Lock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const GENDER_OPTIONS = [
  { value: '', label: 'Prefer not to say' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [gender, setGender] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Password state
  const { changePassword } = useAuth();
  const [passwordOld, setPasswordOld] = useState('');
  const [passwordNew, setPasswordNew] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Initialize from user data
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      // Strip +91 prefix for display
      const rawPhone = user.phone || '';
      setPhone(rawPhone.replace(/^\+91/, ''));
      setGender(user.gender || '');
    }
  }, [user]);

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(val);
    if (val.length > 0 && val.length < 10) setPhoneError('Enter a valid 10-digit number');
    else if (val.length === 10 && !/^[6-9]\d{9}$/.test(val)) setPhoneError('Must start with 6, 7, 8, or 9');
    else setPhoneError('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordNew !== passwordConfirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordNew.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      await changePassword(passwordOld, passwordNew);
      setPasswordSaved(true);
      toast.success('Password changed successfully!');
      setPasswordOld('');
      setPasswordNew('');
      setPasswordConfirm('');
      setTimeout(() => setPasswordSaved(false), 3000);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to change password';
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name cannot be empty'); return; }
    if (phone && !/^[6-9]\d{9}$/.test(phone)) { toast.error('Enter a valid mobile number'); return; }

    setSaving(true);
    try {
      await updateProfile({ name: name.trim(), phone, gender });
      setSaved(true);
      toast.success('Profile updated successfully!');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update profile';
      toast.error(typeof msg === 'string' ? msg : 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  // Check if anything changed
  const hasChanges = user && (
    name.trim() !== (user.name || '') ||
    phone !== (user.phone || '').replace(/^\+91/, '') ||
    gender !== (user.gender || '')
  );

  if (!user) return null;

  const initials = (user.name || user.email || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const joinDate = user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-cream py-10 px-4" data-testid="profile-page">
      <div className="max-w-2xl mx-auto">

        {/* ── Header Card ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1C1209 0%, #3A2510 100%)',
          borderRadius: '24px',
          padding: '40px 36px 36px',
          marginBottom: '28px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative circle */}
          <div style={{
            position: 'absolute',
            top: '-60px', right: '-60px',
            width: '200px', height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(232,140,63,0.2) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-40px', left: '-40px',
            width: '140px', height: '140px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(196,94,42,0.15) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', position: 'relative', zIndex: 1 }}>
            {/* Avatar */}
            <div style={{
              width: '80px', height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #C45E2A, #E88C3F)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.8rem', fontWeight: 700, color: '#FFF8F0',
              fontFamily: "'Playfair Display', serif",
              boxShadow: '0 8px 30px rgba(196,94,42,0.4)',
              flexShrink: 0,
            }}>
              {initials}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 className="font-heading" style={{
                fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
                fontWeight: 800,
                color: '#FFF8F0',
                marginBottom: '4px',
                lineHeight: 1.2,
              }}>
                {user.name || 'Pet Lover'}
              </h1>
              <p className="font-body" style={{ fontSize: '0.88rem', color: '#C45E2A', marginBottom: '8px' }}>
                {user.email}
              </p>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {user.email_verified && (
                  <span className="font-body" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    fontSize: '0.75rem', color: 'rgba(255,248,240,0.5)',
                    background: 'rgba(255,248,240,0.08)',
                    padding: '3px 10px', borderRadius: '100px',
                  }}>
                    <Shield style={{ width: '12px', height: '12px' }} /> Verified
                  </span>
                )}
                {joinDate && (
                  <span className="font-body" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    fontSize: '0.75rem', color: 'rgba(255,248,240,0.5)',
                    background: 'rgba(255,248,240,0.08)',
                    padding: '3px 10px', borderRadius: '100px',
                  }}>
                    <Calendar style={{ width: '12px', height: '12px' }} /> Joined {joinDate}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Edit Form Card ── */}
        <div style={{
          background: '#fff',
          borderRadius: '20px',
          border: '1px solid rgba(196,94,42,0.08)',
          padding: '32px 28px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '28px' }}>
            <div style={{
              width: '40px', height: '40px',
              borderRadius: '12px',
              background: '#F5E8D8',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <PawPrint style={{ width: '20px', height: '20px', color: '#C45E2A' }} />
            </div>
            <div>
              <h2 className="font-heading" style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1C1209' }}>
                Edit Profile
              </h2>
              <p className="font-body" style={{ fontSize: '0.82rem', color: '#8B6A52' }}>
                Update your personal information
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} data-testid="profile-form">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {/* Name */}
              <div>
                <Label className="font-body text-sm text-dark-brown" htmlFor="profile-name">Full Name</Label>
                <div style={{ position: 'relative', marginTop: '6px' }}>
                  <User style={{
                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                    width: '16px', height: '16px', color: '#8B6A52',
                  }} />
                  <Input
                    id="profile-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    className="pl-10 bg-cream/50 border-border-custom"
                    data-testid="profile-name-input"
                    required
                  />
                </div>
              </div>

              {/* Email (read-only) */}
              <div>
                <Label className="font-body text-sm text-dark-brown" htmlFor="profile-email">
                  Email
                </Label>
                <div style={{ position: 'relative', marginTop: '6px' }}>
                  <Mail style={{
                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                    width: '16px', height: '16px', color: '#8B6A52',
                  }} />
                  <Input
                    id="profile-email"
                    value={user.email}
                    className="pl-10 bg-pale/60 border-border-custom text-light-brown cursor-not-allowed"
                    disabled
                    data-testid="profile-email-input"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <Label className="font-body text-sm text-dark-brown" htmlFor="profile-phone">
                  Mobile Number <span className="font-body text-xs text-light-brown font-normal">(optional)</span>
                </Label>
                <div style={{ display: 'flex', marginTop: '6px' }}>
                  <span className="font-body" style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '0 12px', borderRadius: '8px 0 0 8px',
                    border: '1px solid hsl(var(--input))', borderRight: 'none',
                    background: '#FFF8F0', color: '#8B6A52', fontSize: '0.875rem',
                  }}>+91</span>
                  <Input
                    id="profile-phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="10-digit mobile"
                    inputMode="numeric"
                    maxLength={10}
                    className={`rounded-l-none bg-cream/50 border-border-custom ${phoneError ? 'border-red-400' : (phone.length === 10 && !phoneError) ? 'border-green-400' : ''}`}
                    data-testid="profile-phone-input"
                  />
                </div>
                {phoneError && <p className="font-body" style={{ fontSize: '0.75rem', color: '#EF4444', marginTop: '4px' }}>{phoneError}</p>}
              </div>

              {/* Gender */}
              <div>
                <Label className="font-body text-sm text-dark-brown">Gender</Label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '10px',
                  marginTop: '8px',
                }}>
                  {GENDER_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setGender(opt.value)}
                      data-testid={`gender-${opt.value || 'none'}`}
                      className="font-body"
                      style={{
                        padding: '10px 16px',
                        borderRadius: '12px',
                        border: gender === opt.value
                          ? '2px solid #C45E2A'
                          : '1.5px solid rgba(196,94,42,0.12)',
                        background: gender === opt.value
                          ? 'linear-gradient(135deg, rgba(196,94,42,0.08), rgba(232,140,63,0.06))'
                          : '#FFFDF9',
                        color: gender === opt.value ? '#C45E2A' : '#4A3728',
                        fontSize: '0.88rem',
                        fontWeight: gender === opt.value ? 600 : 400,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'center',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div style={{ height: '1px', background: 'rgba(196,94,42,0.08)', margin: '4px 0' }} />

              {/* Submit */}
              <Button
                type="submit"
                disabled={saving || !hasChanges}
                className="w-full bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-12 text-base"
                data-testid="profile-save-btn"
                style={{
                  opacity: (!hasChanges && !saving) ? 0.5 : 1,
                  transition: 'all 0.3s ease',
                }}
              >
                {saving ? (
                  <><span className="spinner" style={{ width: '18px', height: '18px', marginRight: '8px' }} /> Saving...</>
                ) : saved ? (
                  <><CheckCircle2 style={{ width: '18px', height: '18px', marginRight: '8px' }} /> Saved!</>
                ) : (
                  <><Save style={{ width: '18px', height: '18px', marginRight: '8px' }} /> Save Changes</>
                )}
              </Button>

              {!hasChanges && !saving && !saved && (
                <p className="font-body" style={{
                  fontSize: '0.8rem', color: '#8B6A52', textAlign: 'center', marginTop: '-8px',
                }}>
                  Make changes to enable saving
                </p>
              )}
            </div>
          </form>
        </div>

        {/* ── Change Password Card ── */}
        <div style={{
          background: '#fff',
          borderRadius: '20px',
          border: '1px solid rgba(196,94,42,0.08)',
          marginTop: '28px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}>
          {/* Toggle Button */}
          <button
            type="button"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            data-testid="toggle-password-btn"
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '24px 28px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(196,94,42,0.03)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{
              width: '40px', height: '40px',
              borderRadius: '12px',
              background: '#F5E8D8',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Lock style={{ width: '20px', height: '20px', color: '#C45E2A' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 className="font-heading" style={{ fontSize: '1.2rem', fontWeight: 700, color: '#1C1209', margin: 0 }}>
                Change Password
              </h2>
              <p className="font-body" style={{ fontSize: '0.82rem', color: '#8B6A52', margin: 0 }}>
                Ensure your account is using a strong, unique password
              </p>
            </div>
            <ChevronRight style={{
              width: '20px', height: '20px', color: '#8B6A52',
              transition: 'transform 0.3s ease',
              transform: showPasswordForm ? 'rotate(90deg)' : 'rotate(0deg)',
              flexShrink: 0,
            }} />
          </button>

          {/* Collapsible Form */}
          <div style={{
            maxHeight: showPasswordForm ? '500px' : '0',
            opacity: showPasswordForm ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.4s ease, opacity 0.3s ease',
          }}>
            <div style={{ padding: '0 28px 32px' }}>
              <div style={{ height: '1px', background: 'rgba(196,94,42,0.08)', marginBottom: '24px' }} />
              <form onSubmit={handlePasswordSubmit} data-testid="password-form">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  <div>
                    <Label className="font-body text-sm text-dark-brown">Current Password</Label>
                    <div style={{ position: 'relative', marginTop: '6px' }}>
                      <Lock style={{
                        position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                        width: '16px', height: '16px', color: '#8B6A52',
                      }} />
                      <Input
                        type="password"
                        value={passwordOld}
                        onChange={(e) => setPasswordOld(e.target.value)}
                        placeholder="Enter current password"
                        className="pl-10 bg-cream/50 border-border-custom"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="font-body text-sm text-dark-brown">New Password</Label>
                    <div style={{ position: 'relative', marginTop: '6px' }}>
                      <Lock style={{
                        position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                        width: '16px', height: '16px', color: '#8B6A52',
                      }} />
                      <Input
                        type="password"
                        value={passwordNew}
                        onChange={(e) => setPasswordNew(e.target.value)}
                        placeholder="Min 6 chars, 1 uppercase, 1 number"
                        className="pl-10 bg-cream/50 border-border-custom"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="font-body text-sm text-dark-brown">Confirm New Password</Label>
                    <div style={{ position: 'relative', marginTop: '6px' }}>
                      <Lock style={{
                        position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                        width: '16px', height: '16px', color: '#8B6A52',
                      }} />
                      <Input
                        type="password"
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        placeholder="Confirm new password"
                        className="pl-10 bg-cream/50 border-border-custom"
                        required
                      />
                    </div>
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={passwordSaving || !passwordOld || !passwordNew || !passwordConfirm}
                    className="w-full bg-dark-brown hover:bg-dark-brown/90 text-cream font-body rounded-full h-12 text-base mt-2"
                    style={{ transition: 'all 0.3s ease' }}
                  >
                    {passwordSaving ? (
                      <><span className="spinner" style={{ width: '18px', height: '18px', marginRight: '8px' }} /> Updating...</>
                    ) : passwordSaved ? (
                      <><CheckCircle2 style={{ width: '18px', height: '18px', marginRight: '8px' }} /> Updated!</>
                    ) : (
                      <><Save style={{ width: '18px', height: '18px', marginRight: '8px' }} /> Update Password</>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* ── Account Info ── */}
        <div style={{
          marginTop: '20px',
          background: '#FFFDF9',
          borderRadius: '16px',
          border: '1px solid rgba(196,94,42,0.06)',
          padding: '20px 24px',
        }}>
          <p className="font-body" style={{ fontSize: '0.78rem', color: '#8B6A52', textAlign: 'center' }}>
            🐾 Your account is secured with email verification. Contact support if you need to change your email address.
          </p>
        </div>

      </div>
    </div>
  );
}
